/**
 * A11Y Form Observer — WCAG 2.1 AA Compliance
 * FIX-011: Intercetta form iniettati da app Shopify di terze parti
 * e applica dinamicamente aria-label agli input "muti" + stili focus.
 *
 * WCAG References:
 * - 1.1.1 (A): Non-text Content
 * - 1.3.1 (A): Info and Relationships
 * - 4.1.2 (A): Name, Role, Value
 * - 2.4.7 (AA): Focus Visible
 *
 * Branch: fix/wcag-compliance
 * Created: 2026-06-11
 */

(function() {
  'use strict';

  /**
   * Verifica se un elemento input ha già un nome accessibile
   * (via <label for="...">, aria-label, aria-labelledby, title, o placeholder).
   */
  function hasAccessibleName(input) {
    // 1. aria-label o aria-labelledby
    if (input.hasAttribute('aria-label') || input.hasAttribute('aria-labelledby')) {
      return true;
    }

    // 2. <label for="..."> associato
    if (input.id) {
      var label = document.querySelector('label[for="' + input.id + '"]');
      if (label && label.textContent.trim() !== '') {
        return true;
      }
    }

    // 3. Wrapped in <label>
    var parentLabel = input.closest('label');
    if (parentLabel && parentLabel.textContent.trim() !== '') {
      return true;
    }

    // 4. title attribute
    if (input.hasAttribute('title') && input.getAttribute('title').trim() !== '') {
      return true;
    }

    // 5. placeholder (acceptable fallback per WCAG, ma non ideale — lo marchiamo)
    if (input.hasAttribute('placeholder') && input.getAttribute('placeholder').trim() !== '') {
      // Placeholder da solo non è sufficiente per WCAG 3.3.2, ma è meglio di niente
      // Applichiamo comunque aria-label per rinforzare
      return false;
    }

    // 6. type="hidden", type="submit" con value, ecc.
    if (input.type === 'hidden' || input.type === 'submit' || input.type === 'button' || input.type === 'reset') {
      return true; // Questi hanno nomi accessibili nativi
    }

    return false;
  }

  /**
   * Genera un aria-label di fallback per un input.
   */
  function generateFallbackLabel(input) {
    var label = '';

    // 1. Prova placeholder
    if (input.hasAttribute('placeholder')) {
      label = input.getAttribute('placeholder').trim();
      if (label) return label;
    }

    // 2. Prova name attribute (umanizzato)
    if (input.name) {
      label = input.name
        .replace(/^customer\[|\]$/g, '')
        .replace(/\[/g, ' ')
        .replace(/\]/g, '')
        .replace(/\[note\]/g, '')
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (label) return label.charAt(0).toUpperCase() + label.slice(1);
    }

    // 3. Prova il testo adiacente (span, div vicini)
    var parent = input.closest('.form-group, .form-floating, .mc-field-group, .field, .input-group');
    if (parent) {
      var textNodes = parent.querySelectorAll('label, span, .label, .field-label');
      for (var i = 0; i < textNodes.length; i++) {
        var text = textNodes[i].textContent.trim();
        if (text && text.length < 100) {
          return text;
        }
      }
    }

    // 4. Fallback basato sul type
    var typeMap = {
      'email': 'Email',
      'tel': 'Phone',
      'number': 'Number',
      'text': 'Text field',
      'password': 'Password',
      'search': 'Search',
      'url': 'URL',
      'date': 'Date'
    };
    return typeMap[input.type] || 'Form field';
  }

  /**
   * Applica aria-label e stili focus a un singolo input.
   */
  function remediateInput(input) {
    // Skip hidden, submit, button, reset
    if (!input || input.nodeName !== 'INPUT' && input.nodeName !== 'TEXTAREA' && input.nodeName !== 'SELECT') {
      return;
    }
    if (input.type === 'hidden' || input.type === 'submit' || input.type === 'button' || input.type === 'reset') {
      return;
    }

    // Se ha già un nome accessibile, verifico solo gli stili focus
    if (hasAccessibleName(input)) {
      ensureFocusStyles(input);
      return;
    }

    // Genera e applica aria-label
    var fallbackLabel = generateFallbackLabel(input);
    if (fallbackLabel) {
      input.setAttribute('aria-label', fallbackLabel);
    }

    // Assicura stili focus visibili
    ensureFocusStyles(input);
  }

  /**
   * Assicura che l'input non abbia outline: none e che erediti
   * gli stili focus del tema (FIX-004, FIX-006).
   */
  function ensureFocusStyles(input) {
    // Aggiunge una classe per il foglio di remediation se non presente
    if (!input.classList.contains('a11y-focus-checked')) {
      input.classList.add('a11y-focus-checked');

      // Se l'input ha inline style con outline:none, lo rimuove
      if (input.style.outline === 'none' || input.style.outline === '0') {
        input.style.outline = '';
      }
    }
  }

  /**
   * Processa tutti gli input in un container (form o nodo DOM).
   */
  function remediateContainer(container) {
    if (!container) return;

    // Input, textarea, select
    var inputs = container.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), textarea, select');
    for (var i = 0; i < inputs.length; i++) {
      remediateInput(inputs[i]);
    }
  }

  /**
   * Processa tutti i form già presenti al caricamento.
   */
  function remediateAllForms() {
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) {
      remediateContainer(forms[i]);
    }

    // Anche input standalone (es. search bar fuori da form)
    remediateContainer(document.body);

    console.log('[A11Y] Form Observer: ' + document.querySelectorAll('form').length + ' forms scanned. Inputs remediated where needed.');
  }

  /**
   * MutationObserver: intercetta nuovi form iniettati dinamicamente.
   */
  function initObserver() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        // Nuovi nodi aggiunti al DOM
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Se il nodo stesso è un form
            if (node.nodeName === 'FORM' || node.matches === undefined) {
              remediateContainer(node);
            }

            // Se contiene form al suo interno
            if (node.querySelectorAll) {
              var forms = node.querySelectorAll('form');
              for (var i = 0; i < forms.length; i++) {
                remediateContainer(forms[i]);
              }

              // Input standalone aggiunti fuori da form
              var standaloneInputs = node.querySelectorAll('input:not(form input), textarea:not(form textarea), select:not(form select)');
              if (standaloneInputs.length > 0) {
                for (var j = 0; j < standaloneInputs.length; j++) {
                  if (!standaloneInputs[j].closest('form')) {
                    remediateInput(standaloneInputs[j]);
                  }
                }
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    console.log('[A11Y] Form Observer: MutationObserver attivato. Monitoraggio form plugin in corso...');
  }

  // Avvio: DOM già pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      remediateAllForms();
      initObserver();
    });
  } else {
    // DOM già caricato (es. script in fondo alla pagina)
    remediateAllForms();
    initObserver();
  }

})();
